import { useEffect, useRef, useState } from "react";
import { default as WaveSurfer } from "wavesurfer.js";
import { setIsFooterReady } from "../../store/actions/abPlayerStore";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { ColorPalette } from "../../stories/theme";
import { convertWaveformDurationToReadableTime } from "./waveformUtils";
import { useAtomValue } from "jotai";
import { activeWaveformQueryKeyAtom } from "../../atoms/audioAtom";
import { useQueryClient } from "@tanstack/react-query";

export const FOOTER_WAVESURFER_REF_ID = "#waveform-footer-player";
export const DEFAULT_TIME = "0:00";

export const getFooterWaveformRef = () => {
  if (!window.surferidze) return null;
  return window.surferidze[FOOTER_WAVESURFER_REF_ID];
};

export const useGenerateWaveformForFooter = () => {
  const waveformRef = useRef<WaveSurfer | null>(null);
  const { isLooping, loopStartTime, loopEndTime } = useAppSelector(
    (state) => state.abPlayerStore,
  );
  const { queryKey: activeWaveformQueryKey } = useAtomValue(
    activeWaveformQueryKeyAtom,
  );
  const queryClient = useQueryClient();
  const data = queryClient.getQueryData<{
    url: string;
    content_type: string;
    waveform_peaks?: string;
    duration?: number;
  }>(activeWaveformQueryKey);
  const dispatch = useAppDispatch();
  const [duration, setDuration] = useState(DEFAULT_TIME);
  const currentSeekTime = useRef<string>(DEFAULT_TIME);
  const isLoopingRef = useRef(isLooping);
  const loopStartTimeRef = useRef(loopStartTime);
  const loopEndTimeRef = useRef(loopEndTime);

  const waveformDuration = data?.duration;
  const peaks = data?.waveform_peaks;
  const url = data?.url;
  useEffect(() => {
    isLoopingRef.current = isLooping;
    loopStartTimeRef.current = loopStartTime;
    loopEndTimeRef.current = loopEndTime;
  }, [isLooping, loopEndTime, loopStartTime]);

  useEffect(() => {
    if (!url) {
      return;
    }
    const waveFormDiv = document.getElementById(FOOTER_WAVESURFER_REF_ID);
    if (!waveFormDiv) return;

    const generateWaveform = async () => {
      dispatch(setIsFooterReady(false));
      if (window.surferidze) {
        delete window.surferidze[FOOTER_WAVESURFER_REF_ID];
      }

      if (waveformRef.current) {
        waveformRef.current.destroy();
      }
      let waveFormConstructObject = {};

      if (!waveformDuration || !peaks) {
        waveFormConstructObject = { url: url, ...waveFormConstructObject };
      } else {
        waveFormConstructObject = {
          peaks: peaks
            ? [peaks.split(",").map(Number), peaks.split(",").map(Number)]
            : undefined,
          duration: waveformDuration,
          ...waveFormConstructObject,
        };
      }

      currentSeekTime.current = DEFAULT_TIME;
      waveformRef.current = WaveSurfer.create({
        container: waveFormDiv,
        waveColor: ColorPalette.Gray400,
        progressColor: ColorPalette.BoomyOrange400,
        height: 50,
        cursorWidth: 0,
        ...waveFormConstructObject,
      });
      if (!waveformRef.current) return;
      waveformRef.current.setVolume(0);
      if (!window.surferidze) {
        window.surferidze = {};
      }
      window.surferidze[FOOTER_WAVESURFER_REF_ID] = waveformRef.current;
      waveformRef.current.once("ready", async (duration) => {
        setDuration(convertWaveformDurationToReadableTime(duration));
        // Remove the media element so Safari does not conflate the two on the lock screen.
        waveformRef.current?.getMediaElement().remove();
      });

      waveformRef.current.on("destroy", () => {
        waveformRef.current?.unAll();
      });
    };
    void generateWaveform();
    return () => waveformRef.current?.destroy();
  }, [dispatch, url]);

  return {
    waveformRef,
    currentSeekTime,
    duration,
  };
};
